import React, { useState } from 'react';
import DateRangeFields from '../../../components/ui/DateRangeFields';
import { isDateRangeValid } from '../../../shared/dateRange';
import { DURATION_PRESET_LABEL } from '../utils';

// Editable travel-dates section, shown under the map (prototype jody-journey-v2.html
// §dates). Display: a green pill — "📅 1 Jul – 8 Jul 2026 · 7 nights" — with a pencil
// to edit. Edit: two native date inputs (reusing the trip form's single-click-opens
// behaviour) + round check/cancel, consistent with the inline editor convention
// (docs/codebase/ui-conventions.md). Saving PUTs the dates via updateJourneyDates.

interface JourneyDatesProps {
  departureDate?: string | null;
  returnDate?: string | null;
  /** Persist new dates (yyyy-mm-dd or null). Returns true on success. */
  onSave: (departureDate: string | null, returnDate: string | null) => Promise<boolean>;
  busy?: boolean;
  /** The trip brief's rough length ("two_weeks"), shown before "Add travel dates" when there are none. */
  durationPreset?: string | null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const toInput = (iso?: string | null): string => (iso ? String(iso).slice(0, 10) : '');
function fmtDay(value: string): string | null {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

const RoundBtn: React.FC<{ label: string; tone: 'accent' | 'muted'; disabled?: boolean; onClick: () => void; children: React.ReactNode }> = ({ label, tone, disabled, onClick, children }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    style={{ width: 32, height: 32, borderRadius: '50%', background: '#f4f6f3', border: 'none', padding: 0, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1, color: tone === 'accent' ? '#157a55' : '#6c7872', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
  >
    {children}
  </button>
);
const CheckIcon: React.FC = () => (<svg width={15} height={15} viewBox="0 0 24 24" fill="none" aria-hidden><path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" /></svg>);
const XIcon: React.FC = () => (<svg width={15} height={15} viewBox="0 0 24 24" fill="none" aria-hidden><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" /></svg>);
const PencilIcon: React.FC = () => (<svg width={15} height={15} viewBox="0 0 24 24" fill="none" aria-hidden><path d="M14.5 5.5l4 4M3 21l4-1L20 7a2 2 0 0 0-3-3L4 17l-1 4z" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" /></svg>);

const pill: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#e7f1ec', color: '#0e5c40', borderRadius: 14, padding: '11px 14px', fontSize: 14, fontWeight: 700 };

const JourneyDates: React.FC<JourneyDatesProps> = ({ departureDate, returnDate, onSave, busy, durationPreset }) => {
  // Without dates: the brief's rough length first, so the traveller's input
  // shows (Paulo 2026-09-30), then the invitation to set them.
  const rough = !departureDate && !returnDate && durationPreset && durationPreset !== 'flexible' ? DURATION_PRESET_LABEL[durationPreset] : null;
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [saving, setSaving] = useState(false);

  const open = () => {
    setStart(toInput(departureDate));
    setEnd(toInput(returnDate));
    setEditing(true);
  };

  const save = async () => {
    if (!isDateRangeValid(start, end)) return; // guarded; Save is also disabled
    setSaving(true);
    const ok = await onSave(start || null, end || null);
    setSaving(false);
    if (ok) setEditing(false);
  };

  // Read-only label.
  const depLabel = departureDate ? fmtDay(departureDate) : null;
  const retLabel = returnDate ? fmtDay(returnDate) : null;
  const year = (returnDate && new Date(returnDate)) || (departureDate && new Date(departureDate)) || null;
  const yearStr = year && !Number.isNaN(year.getTime()) ? ` ${year.getUTCFullYear()}` : '';
  let range: string | null = null;
  if (depLabel && retLabel) range = `${depLabel} – ${retLabel}${yearStr}`;
  else if (depLabel) range = `${depLabel}${yearStr}`;
  else if (retLabel) range = `Until ${retLabel}${yearStr}`;
  let nights = 0;
  if (departureDate && returnDate) {
    const d = new Date(departureDate); const r = new Date(returnDate);
    if (!Number.isNaN(d.getTime()) && !Number.isNaN(r.getTime())) nights = Math.max(0, Math.round((r.getTime() - d.getTime()) / 86400000));
  }

  if (editing) {
    const invalid = !isDateRangeValid(start, end);
    return (
      <div style={{ ...pill, alignItems: 'flex-start' }}>
        <span aria-hidden style={{ marginTop: 7 }}>📅</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <DateRangeFields
            layout="inline"
            startLabel="Departure"
            endLabel="Return"
            start={start}
            end={end}
            onChange={({ start: s, end: e }) => { setStart(s); setEnd(e); }}
            disabled={saving}
          />
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <RoundBtn label="Save dates" tone="accent" disabled={saving || invalid} onClick={save}><CheckIcon /></RoundBtn>
          <RoundBtn label="Cancel" tone="muted" disabled={saving} onClick={() => setEditing(false)}><XIcon /></RoundBtn>
        </div>
      </div>
    );
  }

  return (
    <div style={pill}>
      <span aria-hidden>📅</span>
      {range ? (
        <>
          <b>{range}</b>
          {nights > 0 && (<><span style={{ color: '#5a8a78', fontWeight: 500 }}>·</span><b>{nights} {nights === 1 ? 'night' : 'nights'}</b></>)}
        </>
      ) : (
        <>
          {rough && <b>{rough}</b>}
          {rough && <span style={{ color: '#5a8a78', fontWeight: 500 }}>·</span>}
          <span style={{ color: '#5a8a78', fontWeight: 500 }}>Add travel dates</span>
        </>
      )}
      <span style={{ marginLeft: 'auto' }}>
        <RoundBtn label="Edit dates" tone="accent" disabled={busy} onClick={open}><PencilIcon /></RoundBtn>
      </span>
    </div>
  );
};

export default JourneyDates;
